import React from 'react'

import 'intl/locale-data/jsonp/th.js'
import _ from 'lodash'
import { FlatList, ListRenderItemInfo, TouchableOpacity } from 'react-native'

import * as util from 'x/utils/util'
import p from 'x/config/platform-specific'
// import ExpireTimer from '../ExpireTimer'
import {
  IBundleProductVariant,
  IOrderProductItem,
  INormalizedShippingOrderProduct,
  INormalizedShippingOrderProducts,
  IOnChangeOrderProductListTable,
} from 'x/types'

import VStack from 'xui/components/VStack'
import HStack from 'xui/components/HStack'
import { COLORS } from 'x/config/styles'

import XButton from './XButton'
import XText from './XText'
import XOverlay from './XOverlay'
import XCustomHeader from './XCustomHeader'
import XCard from './XCard'
import XNumericInput from './XNumericInput'
import Box from './Box'
import XImage from './XImage'
import XIcon from './XIcon'
import XInput from './XInput'

export interface IOrderProductListTableProps {
  isReadOnly?: boolean
  mode?: 'edit' | 'view'

  products: IOrderProductItem[]
  onChange?: (changedData: IOnChangeOrderProductListTable) => void | Promise<void>
}

export interface IOrderProductListTableState {
  isInitialized: boolean
  isDirty: boolean

  editingQtyAt?: {
    ppId: number
    productBundleId?: number
    parentPpId?: number

    editingQty: number
  }

  expandMap: {
    [ppId_productBundleId_parentId: string]: boolean
  }

  detectedProducts?: INormalizedShippingOrderProduct[]
  selectedDetectedProductIndex?: number

  editingShippingOrderProducts: INormalizedShippingOrderProducts

  isCheckAll: boolean
}

class OrderProductListTable extends React.Component<IOrderProductListTableProps, IOrderProductListTableState> {
  static displayName = 'OrderProductListTable'

  constructor(props: IOrderProductListTableProps) {
    super(props)

    this.state = {
      isInitialized: false,
      isDirty: false,
      editingQtyAt: null,

      detectedProducts: null,
      selectedDetectedProductIndex: 0,

      editingShippingOrderProducts: {},
      expandMap: {},
    }
  }

  async componentDidMount(): Promise<void> {
    await this._initializeEditingShipping()

    this.setState({ isInitialized: true })
  }

  _getProductKey = (ppId: number, productBundleId?: number, parentPpId?: number) => {
    if (_.isNumber(productBundleId) && _.isNumber(parentPpId)) {
      return `${ppId}_${productBundleId}_${parentPpId}`
    }

    if (_.isNumber(productBundleId)) {
      return `${ppId}_${productBundleId}`
    }

    return ppId.toString()
  }

  _getNormalizedOrderProductItem = (ppId: number, productBundleId?: number, parentPpId?: number) => {
    const { editingShippingOrderProducts } = this.state
    return editingShippingOrderProducts[this._getProductKey(ppId, productBundleId, parentPpId)]
  }

  _onDataInTableChanged = () => {
    const { onChange } = this.props
    const { editingShippingOrderProducts, isDirty } = this.state

    const isCheckAll = Object.values(editingShippingOrderProducts).every((item) => item.editingQty === item.toShipQty)

    if (_.isFunction(onChange)) {
      onChange({
        data: Object.values(editingShippingOrderProducts),

        sumOfQty: this._getSummaryQtyCount(),
        sumOfEditingQty: this._getSummaryEditingQtyCount(),
        sumOfToShipQty: this._getSummaryToShipQtyCount(),
        sumOfTotalQty: this._getSummaryTotalQtyCount(),

        isDirty,
        isCheckAll,
      })
    }
  }

  // _setNormalizedOrderProductItem = async (ppId: number, productBundleId: number | undefined, item: INormalizedShippingOrderProduct) => {
  //   const { editingShippingOrderProducts } = this.state
  //   const key = this._getProductKey(ppId, productBundleId)

  //   const newEditingShippingOrderProducts = { ...editingShippingOrderProducts, [key]: item }
  //   await util.setStatePromise(this, { editingShippingOrderProducts: newEditingShippingOrderProducts })
  // }

  // To Expost action to ref
  scanSku = (sku: string) => {
    const { editingShippingOrderProducts } = this.state
    const foundProducts = Object.values(editingShippingOrderProducts).filter((item) => item.sku === sku)

    if (foundProducts.length > 1) {
      console.log('Found products:', foundProducts)
      this._openDetectedProductSelectorOverlay(foundProducts)
      return foundProducts
    }

    if (foundProducts.length === 1) {
      const pd = foundProducts[0]
      this._doEditQty(pd.pp_id, pd.editingQty + 1, pd.product_bundle_id, pd.parentPpId)
      return foundProducts
    }

    console.log('Product not found')
    return null
  }

  scanUpc = (upc: string) => {
    const { editingShippingOrderProducts } = this.state
    const foundProducts = Object.values(editingShippingOrderProducts).filter((item) => item.upc === upc)

    if (foundProducts.length > 1) {
      console.log('Found products:', foundProducts)
      this._openDetectedProductSelectorOverlay(foundProducts)
      return foundProducts
    }

    if (foundProducts.length === 1) {
      const pd = foundProducts[0]
      this._doEditQty(pd.pp_id, pd.editingQty + 1, pd.product_bundle_id, pd.parentPpId)
      return foundProducts
    }

    console.log('Product not found')
    return null
  }

  scanBarcode = (skuOrUpcText: string) => {
    const { editingShippingOrderProducts } = this.state
    const foundProducts = Object.values(editingShippingOrderProducts).filter(
      (item) => item.sku === skuOrUpcText || item.upc === skuOrUpcText
    )

    if (foundProducts.length > 1) {
      console.log('Found products:', foundProducts)
      this._openDetectedProductSelectorOverlay(foundProducts)
      return foundProducts
    }

    if (foundProducts.length === 1) {
      const pd = foundProducts[0]
      this._doEditQty(pd.pp_id, pd.editingQty + 1, pd.product_bundle_id, pd.parentPpId)
      return foundProducts
    }

    console.log('Product not found')
    return null
  }

  _initializeEditingShipping = async () => {
    const { products } = this.props
    const editingShippingOrderProducts: INormalizedShippingOrderProducts = {}
    // console.log('_initializeEditingShipping order products:', products)

    const expandMap = {}
    for (let i = 0; i < products.length; i++) {
      const pd = products[i]

      const pdKey = this._getProductKey(pd.pp_id, pd.product_bundle_id)

      editingShippingOrderProducts[pdKey] = {
        pp_id: pd.pp_id,
        productName: pd.name,
        variantName: pd.variant || '',
        fullProductName: `${pd.name}${pd.variant && pd.variant.length ? ` (${pd.variant})` : ''}`,
        imageUrl: pd.thumbnail_uris[0],
        sku: pd.sku,
        upc: pd.upc,
        editingQty: 0,
        qty: pd.shipped_qty,
        totalQty: pd.qty,
        toShipQty: pd.qty - pd.shipped_qty,
        isDirty: false,
      }

      if (pd.product_bundle_id) {
        editingShippingOrderProducts[pdKey].product_bundle_id = pd.product_bundle_id
      }

      const hasBundleProductMappings = pd.bundle_product_mappings && pd.bundle_product_mappings.length > 0

      if (hasBundleProductMappings) {
        editingShippingOrderProducts[pdKey].childrenPpIds = pd.bundle_product_mappings.map((item) => item.pp_id)

        for (let j = 0; j < pd.bundle_product_mappings.length; j++) {
          const childMapping = pd.bundle_product_mappings[j]
          const childKey = this._getProductKey(childMapping.pp_id, childMapping.product_bundle_id, pd.pp_id)
          // console.log('childMapping:', childMapping)
          editingShippingOrderProducts[childKey] = {
            pp_id: childMapping.pp_id,
            productName: childMapping.name,
            variantName: childMapping.variant || '',
            fullProductName: `${childMapping.name}${
              childMapping.variant && childMapping.variant.length ? ` (${childMapping.variant})` : ''
            }`,
            imageUrl: childMapping.thumbnail_uris[0],
            sku: childMapping.sku,
            upc: childMapping.upc,
            editingQty: 0,
            qty: childMapping.shipped_qty,
            toShipQty: childMapping.qty - childMapping.shipped_qty,
            totalQty: childMapping.qty,
            isDirty: false,
            parentPpId: pd.pp_id, // link to parent product
          }

          if (childMapping.product_bundle_id) {
            editingShippingOrderProducts[childKey].product_bundle_id = childMapping.product_bundle_id
          }
        }
      }

      expandMap[pdKey] = true
    }

    // console.log('Editing shipping order products:', editingShippingOrderProducts)
    await util.setStatePromise(this, { editingShippingOrderProducts, expandMap })
    await util.delay(100) // wait for state update
    this._onDataInTableChanged()
  }

  // _isShippedQtyFullfilled = (pd: IOrderProductItem) => {
  //   const { editingShippingOrderProducts } = this.state
  //   const shippingInfo = editingShippingOrderProducts[pd.pp_id]
  //   return shippingInfo && shippingInfo.toShipQty >= shippingInfo.qty
  // }

  // _isShippingFinished = (pd: IOrderProductItem) => {
  //   const { editingShippingOrderProducts } = this.state
  //   const shippingInfo = editingShippingOrderProducts[pd.pp_id]
  //   return shippingInfo && shippingInfo.toShipQty === shippingInfo.qty && shippingInfo.isDirty
  // }

  _onPressCheckBtn = (ppId: number, productBundleId?: number, parentPpId?: number) => {
    const { editingShippingOrderProducts: oldShipping } = this.state
    const pdKey = this._getProductKey(ppId, productBundleId, parentPpId)
    const shippingInfo = oldShipping[pdKey]
    if (shippingInfo) {
      // shippingInfo.isDirty = true
      // shippingInfo.toShipQty = shippingInfo.qty

      // const editingShippingOrderProducts = { ...oldShipping, [ppId]: shippingInfo }
      // this.setState({ editingShippingOrderProducts })
      this._doEditQty(ppId, shippingInfo.toShipQty, productBundleId, parentPpId)
    }
  }

  _onPressUncheckBtn = (ppId: number, productBundleId?: number, parentPpId?: number) => {
    this._doEditQty(ppId, 0, productBundleId, parentPpId)
    // const { editingShippingOrderProducts: oldShipping } = this.state
    // const pdKey = this._getProductKey(ppId, productBundleId)
    // const shippingInfo = oldShipping[pdKey]
    // if (shippingInfo) {
    //   // shippingInfo.isDirty = true
    //   // shippingInfo.toShipQty = 0

    //   // const editingShippingOrderProducts = { ...oldShipping, [ppId]: shippingInfo }
    //   // this.setState({ editingShippingOrderProducts })
    //   this._doEditQty(ppId, 0, shippingInfo.product_bundle_id)
    // }
  }

  _onRequestEditQty = (ppId: number, productBundleId?: number, parentPpId?: number) => {
    console.log('_onRequestEditQty:', ppId, productBundleId, parentPpId)
    const shippingInfo = this._getNormalizedOrderProductItem(ppId, productBundleId, parentPpId)

    if (shippingInfo) {
      this.setState({ editingQtyAt: { ppId, productBundleId, parentPpId, editingQty: shippingInfo.toShipQty } })
    }
  }

  _onRequestCloseEditQtyOverlay = () => {
    this.setState({ editingQtyAt: null })
  }

  _onChangeInputEditQtyText = (text: string) => {
    const { editingQtyAt } = this.state
    if (editingQtyAt) {
      const editingQty = parseInt(text) || 0
      this.setState({ editingQtyAt: { ...editingQtyAt, editingQty } })
    }
  }

  _onSubmitInputQty = async () => {
    const { editingQtyAt } = this.state
    if (editingQtyAt) {
      const { ppId, productBundleId, parentPpId, editingQty } = editingQtyAt
      // const pdKey = this._getProductKey(ppId, parentPpId)
      const pd = this._getNormalizedOrderProductItem(ppId, productBundleId, parentPpId)
      const submitQty = editingQty >= pd.toShipQty ? pd.toShipQty : editingQty

      await this._doEditQty(ppId, submitQty, productBundleId, parentPpId)
      await util.delay(100) // wait for state update
      this._onRequestCloseEditQtyOverlay()
      // await util.delay(100) // wait for state update
      // this._onDataInTableChanged()
      // this.setState({
      //   editingQtyAt: null,
      //   editingShippingOrderProducts: {
      //     ...editingShippingOrderProducts,
      //     [pdKey]: { ...editingShippingOrderProducts[pdKey], qty: editingQty, isDirty: true },
      //   },
      // })
    }
  }

  _doEditQty = async (ppId: number, editingQty: number, productBundleId?: number, parentPpId?: number) => {
    // console.log(`_doEditQty: ppId=${ppId}, editingQty=${editingQty}, parentPpId=${parentPpId}`)

    const { editingShippingOrderProducts } = this.state
    const newEditingShippingOrderProducts = _.cloneDeep(editingShippingOrderProducts)

    const pdKey = this._getProductKey(ppId, productBundleId, parentPpId)
    const pd = editingShippingOrderProducts[pdKey]
    const { toShipQty } = pd

    const isParent = _.isArray(pd.childrenPpIds) && pd.childrenPpIds.length > 0
    const isChild = _.isNumber(pd.parentPpId)

    if (isParent) {
      // Handle parent case
      const children = pd.childrenPpIds || []

      newEditingShippingOrderProducts[pdKey] = {
        ...newEditingShippingOrderProducts[pdKey],
        editingQty,
        isDirty: true,
      }

      children.forEach((childId) => {
        const childKey = this._getProductKey(childId, pd.product_bundle_id, pd.pp_id)
        const childItem = editingShippingOrderProducts[childKey]
        const childTotalQty = childItem && childItem.toShipQty ? childItem.toShipQty : 0
        const unitPerQty = toShipQty > 0 ? childTotalQty / toShipQty : 0

        newEditingShippingOrderProducts[childKey] = {
          ...newEditingShippingOrderProducts[childKey],
          editingQty: Math.floor(editingQty * unitPerQty),
          isDirty: true,
        }
      })
    }

    if (isChild) {
      // Handle child case
      newEditingShippingOrderProducts[pdKey] = {
        ...newEditingShippingOrderProducts[pdKey],
        editingQty,
        isDirty: true,
      }

      const parentKey = this._getProductKey(pd.parentPpId, pd.product_bundle_id)
      const parentPd = this._getNormalizedOrderProductItem(pd.parentPpId, pd.product_bundle_id)
      const parentTotalQty = parentPd && parentPd.toShipQty ? parentPd.toShipQty : 0

      const sumOfChildrenQty = Object.values(newEditingShippingOrderProducts).reduce((acc, item) => {
        if (item.parentPpId === pd.parentPpId) {
          return acc + (item.editingQty || 0)
        }
        return acc
      }, 0)

      const sumOfChildrenTotalQty = Object.values(newEditingShippingOrderProducts).reduce((acc, item) => {
        if (item.parentPpId === pd.parentPpId) {
          return acc + (item.toShipQty || 0)
        }
        return acc
      }, 0)

      const unitPerQty = sumOfChildrenTotalQty > 0 ? parentTotalQty / sumOfChildrenTotalQty : 0
      const reflectToParentQty = Math.floor(sumOfChildrenQty * unitPerQty)
      newEditingShippingOrderProducts[parentKey] = {
        ...newEditingShippingOrderProducts[parentKey],
        editingQty: reflectToParentQty,
        isDirty: true,
      }
    }

    if (!isParent && !isChild) {
      newEditingShippingOrderProducts[pdKey] = {
        ...newEditingShippingOrderProducts[pdKey],
        editingQty,
        isDirty: true,
      }
    }

    // console.log(
    //   `_doEditQty: ppId=${ppId}, editingQty=${editingQty}, parentPpId=${parentPpId} newEditingShippingOrderProducts => `,
    //   newEditingShippingOrderProducts
    // )

    await util.setStatePromise(this, { editingShippingOrderProducts: newEditingShippingOrderProducts, isDirty: true })
    await util.delay(100) // wait for state update
    this._onDataInTableChanged()
  }

  _renderEditQtyOverlay = () => {
    const { editingQtyAt } = this.state
    if (_.isNil(editingQtyAt) || _.isEmpty(editingQtyAt)) return null

    const { ppId, productBundleId, parentPpId, editingQty } = editingQtyAt
    const pd = this._getNormalizedOrderProductItem(ppId, productBundleId, parentPpId)
    const { productName, variantName, qty, toShipQty } = pd

    return (
      <XOverlay visible onRequestClose={this._onRequestCloseEditQtyOverlay}>
        <VStack w='full' bg='white' borderRadius='lg' overflow='hidden'>
          <XCustomHeader
            headerRightProps={{ closeIcon: true, onPressItem: this._onRequestCloseEditQtyOverlay }}
            title='ระบุจำนวนสินค้าที่จัดส่ง'
          />
          <Box w='full' h='1px' borderBottomWidth='1' borderBottomColor='muted.200' />

          <VStack w='full' p='4' space='1'>
            {/* // content begin // */}

            <XText w='full' textAlign='center' numberOfLines={1}>
              {productName}
            </XText>

            <XText w='full' variant='inactive' textAlign='center' numberOfLines={1}>
              {variantName}
            </XText>

            <HStack w='full' h='34px' justifyContent='center' alignItems='center' space='2'>
              <XInput
                placeholder='ระบุจำนวนสินค้า...'
                autoFocus
                textAlign='center'
                focusable
                isInteger
                style={{ flex: 1, maxWidth: 120 }}
                value={editingQty.toString()}
                onChangeText={this._onChangeInputEditQtyText}
                onSubmitEditing={this._onSubmitInputQty}
              />
            </HStack>

            <XText w='full' variant='inactive' textAlign='center'>{`(ระบุจำนวนระหว่าง 0-${toShipQty})`}</XText>

            <HStack w='full' justifyContent='center' alignItems='center' mt='6'>
              <XButton w='100px' onPress={this._onSubmitInputQty} borderRadius='16px'>
                ยืนยัน
              </XButton>
            </HStack>

            {/* // content end // */}
          </VStack>
        </VStack>
      </XOverlay>
    )
  }

  _renderParentProductName = (pd: INormalizedShippingOrderProduct) => {
    const { parentPpId } = pd
    if (!parentPpId) return null

    const parentProduct = this._getNormalizedOrderProductItem(parentPpId, pd.product_bundle_id)

    if (!parentProduct) return null

    return (
      <VStack w='full' space='1'>
        <XText fontSize='xs' variant='inactive'>
          เป็นสมาชิกของสินค้าเซต:{' '}
        </XText>
        <VStack w='full'>
          <XText fontSize='xs' numberOfLines={1}>
            {parentProduct.fullProductName}
          </XText>
        </VStack>
      </VStack>
    )
  }

  _renderChildrenProductNames = (pd: INormalizedShippingOrderProduct) => {
    const { childrenPpIds, product_bundle_id, parentPpId } = pd
    if (!childrenPpIds || childrenPpIds.length === 0) return null

    return (
      <VStack w='full' space='1'>
        <XText fontSize='xs' variant='inactive'>
          มีสินค้าในเซต:{' '}
        </XText>

        <VStack w='full'>
          {childrenPpIds.map((childId) => {
            const childProduct = this._getNormalizedOrderProductItem(childId, product_bundle_id, parentPpId)
            return (
              <XText fontSize='xs' numberOfLines={1} color={COLORS.PRODUCT_BUNDLE_TEXT}>
                {childProduct.fullProductName}
              </XText>
            )
          })}
        </VStack>
      </VStack>
    )
  }

  _renderDetectedProductListItem = (info: ListRenderItemInfo<INormalizedShippingOrderProduct>) => {
    const { selectedDetectedProductIndex } = this.state
    const { item, index } = info
    const { pp_id, productName, variantName, sku, upc, imageUrl, qty, toShipQty, totalQty, childrenPpIds, parentPpId, product_bundle_id } =
      item
    const isChild = _.isNumber(parentPpId)
    // const isShipped = qty >= toShipQty
    const toShipQtyInfoText = `${qty}/${toShipQty}`
    const isEven = index % 2 === 0

    let bgColor = isEven ? 'muted.100' : 'white'
    if (isChild) {
      bgColor = COLORS.PRODUCT_BUNDLE_BG
    }
    // if (isShipped) {
    //   bgColor = 'success.100'
    // }

    const isChecked = selectedDetectedProductIndex === index
    const AlreadyFullfilled = qty >= toShipQty

    return (
      <VStack
        key={`DetectedProduct_${pp_id}_${isChild ? 'child' : 'parent'}`}
        w='full'
        py='0.5'
        // pl={isChild ? '2' : undefined}
        bg={bgColor}
        borderTopWidth='1'
        borderTopColor='muted.400'>
        <TouchableOpacity
          disabled={isChecked || AlreadyFullfilled}
          key={`DetectedProductCheckButton_${index}_${pp_id}`}
          onPress={() => {
            this.setState({ selectedDetectedProductIndex: index })
          }}>
          <HStack w='full'>
            {/* Checkbox */}
            <VStack w='34px' justifyContent='center' alignItems='center' maxWidth='34px' alignSelf='stretch'>
              {AlreadyFullfilled ? null : (
                <XIcon
                  family='FontAwesome'
                  name={isChecked ? 'check-circle' : 'circle-o'}
                  variant={isChecked ? 'success' : 'primary'}
                  color={isChild ? COLORS.PRODUCT_BUNDLE_TEXT : undefined}
                />
              )}
            </VStack>

            {this._renderVerticalSeperator()}

            <HStack alignItems='center' flexWrap='wrap'>
              <Box w='44px' h='44px' borderWidth='1' borderColor='muted.100' borderRadius='lg' overflow='hidden'>
                <XImage width={44} height={44} source={{ uri: imageUrl }} resizeMode='cover' />
              </Box>
            </HStack>

            {/* Product Info */}
            <VStack flex={1} flexWrap='wrap'>
              {/* <XText style={[textColorStyle, textBoldStyle, S.TEXT_LARGER]}>{productFullName}</XText> */}
              <XText numberOfLines={2}>{productName}</XText>
              {variantName && variantName !== '' ? <XText variant='inactive' numberOfLines={1}>{`ตัวเลือก: ${variantName}`}</XText> : null}
              {sku && sku !== '' ? (
                <XText variant='inactive' fontSize='xs' numberOfLines={1} ellipsizeMode='tail'>{`SKU: ${sku}`}</XText>
              ) : null}
              {upc && upc !== '' ? (
                <XText variant='inactive' fontSize='xs' numberOfLines={1} ellipsizeMode='tail'>{`UPC: ${upc}`}</XText>
              ) : null}

              {this._renderParentProductName(item)}
              {this._renderChildrenProductNames(item)}
            </VStack>

            {/* Product Qty */}
            <VStack alignItems='flex-end'>
              {AlreadyFullfilled ? (
                <XText variant='success' fontSize='xs'>
                  ครบแล้ว
                </XText>
              ) : null}

              <XText textAlign='right'>{toShipQtyInfoText}</XText>
            </VStack>
          </HStack>
        </TouchableOpacity>
      </VStack>
    )
  }

  _normalizedProductKeyExtractor = (item: INormalizedShippingOrderProduct, index: number) =>
    `${index}_${item.pp_id}${item.product_bundle_id ? `_${item.product_bundle_id}` : ''}${item.parentPpId ? `_${item.parentPpId}` : ''}`

  _renderDetectedProductSelectorOverlay = () => {
    const { detectedProducts, selectedDetectedProductIndex } = this.state
    if (_.isNil(detectedProducts) || detectedProducts.length === 0) return null

    return (
      <XOverlay visible onRequestClose={this._closeDetectedProductSelectorOverlay}>
        <VStack w='full' bg='white' borderRadius='lg' overflow='hidden'>
          <XCustomHeader
            headerRightProps={{ closeIcon: true, onPressItem: this._closeDetectedProductSelectorOverlay }}
            title='เลือกสินค้าที่สแกนพบ'
          />
          <Box w='full' h='1px' borderBottomWidth='1' borderBottomColor='muted.200' />

          <VStack w='full' p='2' space='1'>
            {/* // content begin // */}
            <XText w='full' variant='inactive' textAlign='center'>
              {`พบสินค้าที่ตรงกับรหัสที่สแกน ${detectedProducts.length} รายการ`}
            </XText>

            <VStack w='full' h='400px' borderWidth='1' borderColor='muted.100' borderRadius='md' overflow='hidden'>
              <FlatList
                style={{ flex: 1, width: '100%' }}
                keyExtractor={this._normalizedProductKeyExtractor}
                data={detectedProducts}
                renderItem={this._renderDetectedProductListItem}
              />
            </VStack>

            <HStack w='full' justifyContent='center' alignItems='center' mt='6'>
              <XButton
                isDisabled={selectedDetectedProductIndex < 0}
                disabled={selectedDetectedProductIndex < 0}
                w='100px'
                onPress={this._confirmDetectedProduct}
                borderRadius='16px'>
                ยืนยัน
              </XButton>
            </HStack>

            {/* // content end // */}
          </VStack>
        </VStack>
      </XOverlay>
    )
  }

  _openDetectedProductSelectorOverlay = (detectedProducts: INormalizedShippingOrderProduct[]) => {
    this.setState({ detectedProducts, selectedDetectedProductIndex: -1 })
  }

  _closeDetectedProductSelectorOverlay = () => {
    this.setState({ detectedProducts: null, selectedDetectedProductIndex: -1 })
  }

  _confirmDetectedProduct = async () => {
    const { detectedProducts, selectedDetectedProductIndex } = this.state
    if (!detectedProducts || selectedDetectedProductIndex < 0) return

    const selectedProduct = detectedProducts[selectedDetectedProductIndex]
    console.log('_confirmDetectedProduct detected product:', selectedProduct)

    const newQty = selectedProduct.editingQty + 1 > selectedProduct.toShipQty ? selectedProduct.toShipQty : selectedProduct.editingQty + 1

    await this._doEditQty(selectedProduct.pp_id, newQty, selectedProduct.product_bundle_id, selectedProduct.parentPpId)
    this._closeDetectedProductSelectorOverlay()
  }

  // _renderBundleOrderProductListItem = (pd: IOrderProductItem, bp: IBundleProductVariant, index: number) => {
  //   const pdName = bp.pt_name || ''
  //   const pdVariantName = bp.pp_name || ''

  //   const { isReadOnly } = this.props
  //   const { editingShippingOrderProducts } = this.state
  //   const qty = editingShippingOrderProducts[bp.pp_id]?.editingQty || bp.editingQty - bp.shipped_qty
  //   const toShipQty = editingShippingOrderProducts[bp.pp_id]?.toShipQty || 0
  //   const isShipped = qty === 0
  //   const toShipQtyInfoText = `${toShipQty}/${qty}`

  //   const isEven = index % 2 === 0

  //   // let bgColor = isEven ? 'muted.200' : 'white'
  //   // if (isShipped) {
  //   //   bgColor = 'success.100'
  //   // }

  //   return (
  //     <VStack
  //       w='full'
  //       // bg={bgColor}
  //       borderTopWidth='1'
  //       borderTopColor='muted.200'>
  //       <HStack w='full' pl='4' space='1'>
  //         {/* Checkbox */}
  //         {isReadOnly ? null : (
  //           <VStack
  //             w='34px'
  //             justifyContent='center'
  //             alignItems='center'
  //             maxWidth='34px'
  //             alignSelf='stretch'
  //             borderRightColor='muted.200'
  //             borderRightWidth='1'>
  //             <TouchableOpacity
  //               key={`CheckButton_${index}_${bp.pp_id}`}
  //               onPress={() => {
  //                 if (isShipped) {
  //                   this._onPressUncheckBtn(pd)
  //                 } else {
  //                   this._onPressCheckBtn(pd)
  //                 }
  //               }}>
  //               <XIcon family='FontAwesome' name={isShipped ? 'check-circle' : 'circle-o'} variant={isShipped ? 'success' : 'primary'} />
  //             </TouchableOpacity>
  //           </VStack>
  //         )}

  //         <HStack alignItems='center' flexWrap='wrap'>
  //           <XImage width={44} height={44} source={{ uri: bp.tiny_img_url }} resizeMode='cover' />
  //         </HStack>

  //         {/* Product Info */}
  //         <VStack flex={1} flexWrap='wrap'>
  //           {/* <XText style={[textColorStyle, textBoldStyle, S.TEXT_LARGER]}>{productFullName}</XText> */}
  //           <XText numberOfLines={2}>{pdName}</XText>
  //           {pdVariantName !== '' ? <XText variant='inactive' numberOfLines={1}>{`ตัวเลือก: ${pdVariantName}`}</XText> : null}
  //           {bp.sku && bp.sku !== '' ? (
  //             <XText variant='inactive' fontSize='xs' numberOfLines={1} ellipsizeMode='tail'>{`SKU: ${bp.sku}`}</XText>
  //           ) : null}
  //           {bp.upc && bp.upc !== '' ? (
  //             <XText variant='inactive' fontSize='xs' numberOfLines={1} ellipsizeMode='tail'>{`UPC: ${bp.upc}`}</XText>
  //           ) : null}
  //         </VStack>

  //         {/* Product Qty */}
  //         <VStack alignItems='flex-end'>
  //           {isShipped && isReadOnly ? (
  //             <>
  //               <XText variant='success' textAlign='right' bold>
  //                 จัดส่งแล้ว
  //               </XText>
  //               <XText variant='inactive' textAlign='right'>{`(${toShipQty}/${qty})`}</XText>
  //             </>
  //           ) : null}

  //           {!isShipped && !isReadOnly ? <XText textAlign='right'>{toShipQtyInfoText}</XText> : null}

  //           {!isShipped && !isReadOnly ? (
  //             <TouchableOpacity onPress={() => this._onRequestEditQty(bp.pp_id)}>
  //               <XIcon name='edit' family='FontAwesome' />
  //             </TouchableOpacity>
  //           ) : null}
  //         </VStack>
  //       </HStack>
  //     </VStack>
  //   )
  // }

  // _renderBundleOrderProductListItems = (pd: IOrderProductItem, index: number) => {
  //   const { bundle_product_mappings } = pd
  //   if (_.isNil(bundle_product_mappings) || bundle_product_mappings.length === 0) return null

  //   const isCollapsed = false

  //   return (
  //     <VStack key={`Bundle_${index}_${pd.pp_id}`} w='full' bg={COLORS.PRODUCT_BUNDLE_BG} borderTopWidth='1' borderTopColor='muted.200'>
  //       {bundle_product_mappings.map((bp, idx) => this._renderBundleOrderProductListItem(pd, bp, idx))}
  //     </VStack>
  //   )
  // }

  _renderOrderProductListItem = (pd: IOrderProductItem, index: number) => {
    // const normalizedItem = this._getNormalizedOrderProductItem(pd.pp_id, pd.product_bundle_id)
    const normalizedItem = this._getNormalizedOrderProductItem(pd.pp_id, pd.product_bundle_id)
    return this._renderNormalizedOrderProductListItem(normalizedItem, index)
  }

  _isExpand = (pdKey: string) => {
    const { expandMap } = this.state
    return _.isBoolean(expandMap[pdKey]) ? expandMap[pdKey] : true
  }

  _renderChildrenOrderProductListItems = (childrenPpIds: number[], productBundleId?: number, parentPpId?: number) => {
    if (!childrenPpIds || childrenPpIds.length === 0) return null

    const pdKey = this._getProductKey(parentPpId, productBundleId)
    const isExpanded = this._isExpand(pdKey)

    if (!isExpanded) return null

    // console.log('Render children items:', childrenPpIds, productBundleId, parentPpId)
    return childrenPpIds.map((ppId, index) => {
      const normalizedItem = this._getNormalizedOrderProductItem(ppId, productBundleId, parentPpId)
      // console.log('Render child item:', normalizedItem)
      return this._renderNormalizedOrderProductListItem(normalizedItem, index)
    })
  }

  _renderNormalizedOrderProductListItem = (pd: INormalizedShippingOrderProduct, index: number) => {
    if (!pd) return null

    const { isReadOnly, mode = 'view' } = this.props
    const {
      pp_id,
      productName,
      variantName = '',
      imageUrl,
      sku,
      upc,
      qty,
      editingQty,
      toShipQty,
      totalQty,
      childrenPpIds,
      product_bundle_id,
      parentPpId,
    } = pd

    const isShipped = editingQty >= toShipQty
    const toShipQtyInfoText = `${editingQty}/${toShipQty}`
    const isEven = index % 2 === 0
    const isChild = _.isNumber(pd.parentPpId)

    let bgColor = isEven ? 'muted.100' : 'white'
    if (isChild) {
      bgColor = COLORS.PRODUCT_BUNDLE_BG
    }
    if (isShipped) {
      bgColor = 'success.100'
    }

    return (
      <VStack
        key={`OrderProduct_${pp_id}_${isChild ? 'child' : 'parent'}`}
        w='full'
        px='1'
        pt='1'
        // pl={isChild ? '2' : undefined}
        bg={bgColor}
        borderTopWidth='1'
        borderTopColor={isChild ? 'muted.200' : 'gray.400'}>
        <HStack w='full'>
          {/* Checkbox */}
          {isReadOnly || mode === 'view' ? null : (
            <VStack
              w={isChild ? '68px' : '34px'}
              h={p.op.isWeb() ? '100%' : undefined}
              justifyContent='center'
              alignItems='flex-end'
              alignSelf='stretch'>
              <TouchableOpacity
                key={`CheckButton_${index}_${pp_id}`}
                style={{ width: '100%', height: p.op.isWeb() ? '100%' : undefined, justifyContent: 'center', alignItems: 'flex-end' }}
                onPress={() => {
                  if (isShipped) {
                    this._onPressUncheckBtn(pp_id, product_bundle_id, parentPpId)
                  } else {
                    this._onPressCheckBtn(pp_id, product_bundle_id, parentPpId)
                  }
                }}>
                <XIcon
                  family='FontAwesome'
                  name={isShipped ? 'check-circle' : 'circle-o'}
                  variant={isShipped ? 'success' : 'primary'}
                  color={isChild ? COLORS.PRODUCT_BUNDLE_TEXT : undefined}
                />
              </TouchableOpacity>
            </VStack>
          )}

          {isReadOnly || mode === 'view' ? null : this._renderVerticalSeperator()}

          <HStack px='1' alignItems='center' flexWrap='wrap'>
            <Box w='44px' h='44px' borderWidth='1' borderColor='muted.100' borderRadius='lg' overflow='hidden'>
              <XImage width={44} height={44} source={{ uri: imageUrl }} resizeMode='cover' />
            </Box>
          </HStack>

          {/* Product Info */}
          <VStack flex={1} flexWrap='wrap'>
            {/* <XText style={[textColorStyle, textBoldStyle, S.TEXT_LARGER]}>{productFullName}</XText> */}
            <XText numberOfLines={2}>{productName}</XText>
            {variantName && variantName !== '' ? <XText variant='inactive' numberOfLines={1}>{`ตัวเลือก: ${variantName}`}</XText> : null}
            {sku && sku !== '' ? (
              <XText variant='inactive' fontSize='xs' numberOfLines={1} ellipsizeMode='tail'>{`SKU: ${sku}`}</XText>
            ) : null}
            {upc && upc !== '' ? (
              <XText variant='inactive' fontSize='xs' numberOfLines={1} ellipsizeMode='tail'>{`UPC: ${upc}`}</XText>
            ) : null}
          </VStack>

          {/* Product Qty */}
          {mode === 'edit' ? (
            <VStack alignItems='flex-end'>
              {/* {isShipped && isReadOnly ? (
                <XText variant='success' textAlign='right' bold>
                  จัดส่งแล้ว
                </XText>
              ) : null} */}

              <XText textAlign='right'>{toShipQtyInfoText}</XText>

              {!isReadOnly ? (
                <TouchableOpacity onPress={() => this._onRequestEditQty(pp_id, product_bundle_id, parentPpId)}>
                  <XIcon name='edit' family='FontAwesome' color={isChild ? COLORS.PRODUCT_BUNDLE_TEXT : undefined} />
                </TouchableOpacity>
              ) : null}
            </VStack>
          ) : (
            <HStack w='177px' alignItems='center' justifyContent='flex-end'>
              {this._renderVerticalSeperator()}
              <XText w='50px' textAlign='right'>
                {totalQty}
              </XText>
              {this._renderVerticalSeperator()}
              <XText w='50px' textAlign='right'>
                {qty}
              </XText>
              {this._renderVerticalSeperator()}
              <XText w='50px' textAlign='right'>
                {toShipQty}
              </XText>
            </HStack>
          )}
        </HStack>
        <VStack w='full'>
          {this._renderExpandableButton(pd)}
          {/* <XText>{JSON.stringify(pd.bundle_product_mappings)}</XText> */}
          {/* render sub-items */}
          {/* {this._renderBundleOrderProductListItems(pd, index)} */}
          {this._renderChildrenOrderProductListItems(childrenPpIds, pd.product_bundle_id, pd.pp_id)}
        </VStack>
      </VStack>
    )
  }

  _onPressToggleExpand = (ppId: number, productBundleId: number) => {
    const pdKey = this._getProductKey(ppId, productBundleId)

    this.setState((prevState) => ({
      expandMap: {
        ...prevState.expandMap,
        [pdKey]: !prevState.expandMap[pdKey],
      },
    }))
  }

  _renderExpandableButton = (pd: INormalizedShippingOrderProduct) => {
    if (_.isNumber(pd.parentPpId) || !_.isArray(pd.childrenPpIds) || pd.childrenPpIds.length === 0) {
      return null
    }

    const pdKey = this._getProductKey(pd.pp_id, pd.product_bundle_id)
    const isExpanded = this._isExpand(pdKey)

    return (
      <VStack
        w='full'
        py='1'
        // borderTopWidth='1' borderTopColor='muted.200'
        // style={
        //   isExpanded
        //     ? {
        //         position: 'absolute',
        //         top: -20,
        //         left: 0,
        //         right: 0,
        //         zIndex: 999,
        //       }
        //     : {}
        // }
        justifyContent='center'
        alignItems='center'>
        <TouchableOpacity onPress={() => this._onPressToggleExpand(pd.pp_id, pd.product_bundle_id)}>
          <HStack
            bg={COLORS.PRODUCT_BUNDLE_BG}
            px='2'
            py='1px'
            space='1'
            alignItems='center'
            justifyContent='center'
            borderWidth='1'
            borderColor={COLORS.PRODUCT_BUNDLE_TEXT}
            borderRadius='full'>
            <HStack minW='200px' alignItems='center' justifyContent='space-between'>
              <XText fontSize='xs' color={COLORS.PRODUCT_BUNDLE_TEXT}>
                {isExpanded ? 'ซ่อนสินค้าในเซต' : 'ดูสินค้าในเซต'}
              </XText>
              <XText fontSize='xs' color={COLORS.PRODUCT_BUNDLE_TEXT}>{`สินค้า: ${pd.childrenPpIds.length} จำนวน: ${pd.childrenPpIds.reduce(
                (acc, id) => acc + (this.state.editingShippingOrderProducts[id]?.totalQty || 0),
                0
              )}`}</XText>
            </HStack>

            <XIcon color={COLORS.PRODUCT_BUNDLE_TEXT} name={isExpanded ? 'chevron-up' : 'chevron-down'} family='MaterialCommunityIcons' />
          </HStack>
        </TouchableOpacity>
      </VStack>
    )
  }

  _renderOrderProductList = () => {
    const { products } = this.props

    return products.map(this._renderOrderProductListItem)
  }

  checkAllProducts = async () => {
    const { editingShippingOrderProducts } = this.state
    const newEditingShippingOrderProducts = { ...editingShippingOrderProducts }

    Object.values(editingShippingOrderProducts).forEach((item) => {
      const isChild = _.isNumber(item.parentPpId)
      const pdKey = this._getProductKey(item.pp_id, item.product_bundle_id, isChild ? item.parentPpId : undefined)
      newEditingShippingOrderProducts[pdKey] = {
        ...item,
        editingQty: item.toShipQty,
      }
    })

    await util.setStatePromise(this, { editingShippingOrderProducts: newEditingShippingOrderProducts, isDirty: true })
    await util.delay(100) // wait for state update
    this._onDataInTableChanged()
  }

  _onPressToggleCheckAllProducts = async () => {
    const { editingShippingOrderProducts } = this.state
    const newEditingShippingOrderProducts = { ...editingShippingOrderProducts }
    const toShipQty = this._getSummaryToShipQtyCount()
    const sumQty = this._getSummaryEditingQtyCount()

    Object.values(editingShippingOrderProducts).forEach((item) => {
      const isChild = _.isNumber(item.parentPpId)
      const pdKey = this._getProductKey(item.pp_id, item.product_bundle_id, isChild ? item.parentPpId : undefined)
      newEditingShippingOrderProducts[pdKey] = {
        ...item,
        editingQty: sumQty >= toShipQty ? 0 : item.toShipQty,
      }
    })

    await util.setStatePromise(this, { editingShippingOrderProducts: newEditingShippingOrderProducts, isDirty: true })
    await util.delay(100) // wait for state update
    this._onDataInTableChanged()
  }

  _getSummaryTotalQtyCount = () => {
    const { editingShippingOrderProducts } = this.state

    const totalCount = Object.values(editingShippingOrderProducts).reduce((acc, item) => {
      if (!item.childrenPpIds || item.childrenPpIds.length === 0) {
        return acc + item.totalQty
      }
      return acc
    }, 0)

    return totalCount
  }

  _getSummaryQtyCount = () => {
    const { editingShippingOrderProducts } = this.state

    const qtyCount = Object.values(editingShippingOrderProducts).reduce((acc, item) => {
      if (!item.childrenPpIds || item.childrenPpIds.length === 0) {
        return acc + item.qty
      }
      return acc
    }, 0)

    return qtyCount
  }

  _getSummaryEditingQtyCount = () => {
    const { editingShippingOrderProducts } = this.state

    const scannedCount = Object.values(editingShippingOrderProducts).reduce((acc, item) => {
      if (!item.childrenPpIds || item.childrenPpIds.length === 0) {
        return acc + item.editingQty
      }
      return acc
    }, 0)

    return scannedCount
  }

  _getSummaryToShipQtyCount = () => {
    const { editingShippingOrderProducts } = this.state

    const sumOfTotalQty = Object.values(editingShippingOrderProducts).reduce((acc, item) => {
      if (!item.childrenPpIds || item.childrenPpIds.length === 0) {
        return acc + item.toShipQty
      }
      return acc
    }, 0)

    return sumOfTotalQty
  }

  _renderProductTable = () => {
    const { mode = 'view' } = this.props
    if (mode === 'edit') {
      return null
    }

    return (
      <HStack w='full' px='1' h='34px' alignItems='center' borderTopWidth='1' borderTopColor='muted.200'>
        <VStack w='34px' alignSelf='stretch' />
        <HStack flex={1} justifyContent='center' alignItems='center'>
          <XText>รายการสินค้า</XText>
        </HStack>

        <HStack h='34px' w='177px' alignItems='center' justifyContent='flex-end'>
          {this._renderVerticalSeperator()}
          <XText w='50px' fontSize='xs' textAlign='right'>
            ทั้งหมด
          </XText>
          {this._renderVerticalSeperator()}
          <XText w='50px' fontSize='xs' textAlign='right'>
            ส่งแล้ว
          </XText>
          {this._renderVerticalSeperator()}
          <XText w='50px' fontSize='xs' textAlign='right'>
            รอจัดส่ง
          </XText>
        </HStack>
      </HStack>
    )
  }

  _renderProductTableCheackAllBar = () => {
    const { mode = 'view', isReadOnly } = this.props
    if (mode === 'view') {
      return null
    }

    const isCheckAll = this._getSummaryEditingQtyCount() >= this._getSummaryToShipQtyCount()

    return (
      <VStack w='full' px='1'>
        <HStack w='full' h='34px' alignItems='center' borderTopWidth='1' borderTopColor='muted.200'>
          {isReadOnly ? null : (
            <VStack w='34px' justifyContent='center' alignItems='center' maxWidth='34px' alignSelf='stretch'>
              <TouchableOpacity disabled={isReadOnly} onPress={this._onPressToggleCheckAllProducts}>
                <XIcon
                  variant={isCheckAll ? 'success' : 'inactive'}
                  name={isCheckAll ? 'checkbox-multiple-marked-circle' : 'checkbox-blank-circle-outline'}
                  family='MaterialCommunityIcons'
                />
              </TouchableOpacity>
            </VStack>
          )}

          {isReadOnly ? null : this._renderVerticalSeperator()}

          <HStack flex={1} alignItems='center'>
            <XText variant='inactive'>{`ยืนยันจำนวนแล้ว ${this._getSummaryEditingQtyCount()} จาก ${this._getSummaryToShipQtyCount()}`}</XText>
          </HStack>
        </HStack>
      </VStack>
    )
  }

  _renderVerticalSeperator = () => {
    return (
      <VStack w='9px' h={p.op.isWeb() ? '100%' : undefined} justifyContent='center' alignItems='center'>
        <Box w='1px' h={p.op.isWeb() ? '80%' : undefined} borderRightColor='muted.200' borderRightWidth='1' />
      </VStack>
    )
  }

  render() {
    const { isInitialized } = this.state
    if (!isInitialized) return null

    return (
      <VStack w='full'>
        {this._renderProductTable()}
        {this._renderProductTableCheackAllBar()}
        {this._renderOrderProductList()}
        {this._renderEditQtyOverlay()}
        {this._renderDetectedProductSelectorOverlay()}
      </VStack>
    )
  }
}

export default OrderProductListTable
